<template>
  <div
      class="fixed left-0 top-0 z-40 h-full w-full bg-black/40"
      ref="overlay"
      @click="onClose"
      v-if="open"
  />

  <Transition
      enter-from-class="opacity-0"
      enter-active-class="duration-500 transition-opacity"
      leave-to-class="opacity-0"
      leave-active-class="duration-500 transition-opacity"
  >
    <div
        v-if="open"
        class="fixed right-0 top-0 z-50 h-full w-11/12 overflow-y-scroll bg-grey-10 p-10"
    >
      <div class="absolute top-8 right-8">
        <IconCross
            @click="onClose"
            class="h-8 w-6 cursor-pointer text-grey-50"
        />
      </div>

      <ContactStageIndicator
          v-if="showStageIndicator"
          :stage="stage"
          @gotoBranchSelect="onGotoBranchSelect"
      />

      <AcCard
          v-if="error"
          class="mb-4 rounded-md bg-red-500 p-2 font-bold text-white"
      >
        {{
          formErrors['error']?.[0] ??
          'An error has occurred while submitting your contact.'
        }}
      </AcCard>

      <div class="md:w-8/12">
        <ContactUserTypeForm
            v-if="currentView === ContactForm.CONTACT_FORM_USER_TYPE"
            @selectType="onSelectType"
        />
        <ContactBranchSelectForm
            v-if="currentView === ContactForm.CONTACT_FORM_BRANCH_SELECT"
            :agency="agency"
            :previous-branch="previousBranch"
            :locations="branches"
            :loading="loadingBranches"
            @selectBranch="onSelectBranch"
        />
        <ChooseOtherContactOption
            v-if="currentView === ContactForm.CONTACT_CHOOSE_OTHER_OPTION"
            :agency="agency"
            :branch="branch"
            :user-type="UserType"
            :has-email="hasEmail"
            :has-telephone="hasTelephone"
            :has-website="hasWebsite"
            @closeClick="onClose"
            @selectOtherOption="onSelectOtherOption"
        />

        <ContactCandidateDetailsForm
            v-if="currentView === ContactForm.CONTACT_FORM_CANDIDATE_DETAILS"
            @submitForm="onSubmitForm"
            @changeParameter="setParameter"
            :errors="formErrors"
            :first-name="parameters.firstName"
            :surname="parameters.surname"
            :job-title="parameters.jobTitle"
            :email-address="parameters.emailAddress"
            :telephone-number="parameters.telephoneNumber"
            :sharing-consent="!!parameters.sharingConsent"
        />
        <ContactCandidateEmailForm
            v-if="currentView === ContactForm.CONTACT_FORM_CANDIDATE_EMAIL"
            @submitForm="onSubmitForm"
            @changeParameter="setParameter"
            :errors="formErrors"
            :agency="agency"
            :preferred-job-role="parameters.preferredJobRole"
            :preferred-location="parameters.preferredLocation"
            :salary-expectation-value="parameters.salaryExpectationValue"
            :salary-expectation-unit="parameters.salaryExpectationUnit"
            :emailContent="parameters.emailContent"
            :first-name="parameters.firstName"
            :surname="parameters.surname"
            :job-title="parameters.jobTitle"
            :email-address="parameters.emailAddress"
            :telephone-number="parameters.telephoneNumber"
            :cv-file="parameters.cvFile"
        />

        <!-- Employer Section -->

        <ContactEmployerDetailsForm
            v-if="currentView === ContactForm.CONTACT_FORM_EMPLOYER_DETAILS"
            @submitForm="onSubmitForm"
            @skipForm="onSkipForm"
            @changeParameter="setParameter"
            @submitPreregister="onSubmitPreregister"
            :errors="formErrors"
            :employer-name="parameters.employerName"
        />

        <ContactEmployerEmailForm
            v-if="currentView === ContactForm.CONTACT_FORM_EMPLOYER_EMAIL"
            @submitForm="onSubmitForm"
            @changeParameter="setParameter"
            :agency="agency"
            :errors="formErrors"
            :job-title="parameters.jobTitle"
            :job-location="parameters.jobLocation"
            :employment-type="parameters.employmentType"
            :enquiry="parameters.enquiry"
            :email-address="parameters.emailAddress"
            :company-name="parameters.companyName"
            :contact-name="parameters.contactName"
            :telephone-number="parameters.telephoneNumber"
            :job-specification="parameters.jobSpecification"
        />

        <ContactTelfaxSuccess
            v-if="currentView === ContactForm.CONTACT_FORM_TELFAX_SUCCESS"
            :agency="agency"
            :branch="branch"
            :user-type="UserType"
            @closeClick="onClose"
            @registerContact="onRegisterContact"
        />

        <ContactWebsiteSuccess
            v-if="currentView === ContactForm.CONTACT_FORM_WEBSITE_SUCCESS"
            @submitPreregister="onSubmitPreregister"
            :agency="agency"
            :user-type="parameters.emp_cand"
            @closeClick="onClose"
            @registerContact="onRegisterContact"
        />

        <ContactSocialSuccess
            v-if="currentView === ContactForm.CONTACT_FORM_SOCIAL_SUCCESS"
            @submitPreregister="onSubmitPreregister"
            :agency="agency"
            :branch="branch"
            :user-type="parameters.emp_cand"
            :socialType="contactSocialType"
            @closeClick="onClose"
            @registerContact="onRegisterContact"
        />

        <ContactEmailSuccess
            v-if="currentView === ContactForm.CONTACT_FORM_EMAIL_SUCCESS"
            :agency="agency"
            @closeClick="onClose"
            @registerContact="onRegisterContact"
        />
      </div>
    </div>
  </Transition>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import { cookies } from 'Mixins/cookies';
import ContactStageIndicator from 'Components/ContactStageIndicator';
import ContactCandidateDetailsForm from 'Components/ContactCandidateDetailsForm';
import ContactCandidateEmailForm from 'Components/ContactCandidateEmailForm';
import ContactForm from 'Helpers/ContactForm';
import ContactUserTypeForm from 'Components/ContactUserTypeForm';
import ContactBranchSelectForm from 'Components/ContactBranchSelectForm';
import ContactEmployerEmailForm from 'Components/ContactEmployerEmailForm';
import ContactEmployerDetailsForm from 'Components/ContactEmployerDetailsForm';
import ContactTelfaxSuccess from 'Components/ContactTelfaxSuccess';
import ContactWebsiteSuccess from 'Components/ContactWebsiteSuccess';
import ContactSocialSuccess from 'Components/ContactSocialSuccess';
import ContactEmailSuccess from 'Components/ContactEmailSuccess';
import AcCard from 'Components/AcCard';
import IconCross from "Components/Icons/IconCross.vue";
import ChooseOtherContactOption from "Components/ChooseOtherContactOption.vue";

export default {
  name: 'ContactSystem',
  components: {
    ChooseOtherContactOption,
    IconCross,
    AcCard,
    ContactEmailSuccess,
    ContactWebsiteSuccess,
    ContactSocialSuccess,
    ContactTelfaxSuccess,
    ContactEmployerDetailsForm,
    ContactEmployerEmailForm,
    ContactBranchSelectForm,
    ContactUserTypeForm,
    ContactCandidateEmailForm,
    ContactCandidateDetailsForm,
    ContactStageIndicator,
  },
  mixins: [cookies],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('contact', [
      'error',
      'agency',
      'parameters',
      'branch',
      'previousBranch',
      'branches',
      'loadingBranches',
      'formErrors',
      'contactFormType',
      'contactSocialType',
      'bypassCandidateInformation',
    ]),
    ...mapGetters('contact', ['currentView', 'stage', 'showStageIndicator']),
    ContactForm() {
      return ContactForm;
    },
    UserType() {
      return this.parameters.emp_cand ?? this.getCookie('requestor_type');
    },
    hasEmail() {
      const isCandidate = this.UserType === 'cnd';
      const isEmployer = this.UserType === 'emp';
      if (!isCandidate && !isEmployer) return false;

      return isCandidate
          ? (this.agency?.CandEmail > 0)
          : (this.agency?.EmpEmail > 0);
    },

    /** Returns true if the agency has a telephone number for the user’s type. */
    hasTelephone() {
      const isCandidate = this.UserType === 'cnd';
      const isEmployer = this.UserType === 'emp';
      if (!isCandidate && !isEmployer) return false;

      return isCandidate
          ? (this.agency?.CandTelFax > 0)
          : (this.agency?.EmpTelFax > 0);
    },

    /** Returns true if the agency has a website for the user’s type. */
    hasWebsite() {
      const isCandidate = this.UserType === 'cnd';
      const isEmployer = this.UserType === 'emp';
      if (!isCandidate && !isEmployer) return false;

      return isCandidate
          ? (this.agency?.CandWebsite > 0)
          : (this.agency?.EmpWebsite > 0);
    },
    AgencyHasData() {
      if (!this.agency) return false;

      const isCandidate = this.UserType === 'cnd';
      const isEmployer = this.UserType === 'emp';

      switch (this.currentView) {
        case 'contactFormTelfaxSuccess':
          return isCandidate ? !(this.agency.CandTelFax > 0) : isEmployer ? !(this.agency.EmpTelFax > 0) : false;
        case 'contactFormWebsiteSuccess':
          return isCandidate ? !(this.agency.CandWebsite > 0) : isEmployer ? !(this.agency.EmpWebsite > 0) : false;
        case 'contactFormCandidateEmail':
          return isCandidate && !(this.agency.CandEmail > 0);
        case 'contactFormEmployerEmail':
          return isEmployer && !(this.agency.EmpEmail > 0);
        default:
          return false;
      }
    }
  },
  watch: {
    branch(newVal) {
    //   console.log(`"Branch watch: newVal=${newVal}, formType=${this.contactFormType}`);
    //   if (
    //       newVal
    //       && this.parameters.emp_cand === 'cnd'
    //       && this.bypassCandidateInformation
    //       && (
    //           this.contactFormType === 'telfax'
    //           || this.contactFormType === 'website'
    //           || this.contactFormType === 'social'
    //       )
    //   ) {
    //     console.log("Bypassing candidate info");
    //     this.bypassCandidateForm();
    //   }
    },
    open(newVal) {
      if (newVal) {
        this.disableScroll();
      } else {
        this.enableScroll();
      }
    },
    AgencyHasData(newVal) {
      if (newVal) {
        this.setAgencyHasNoData(newVal)
        this.setContactFormType('other');
      }
    }
  },
  methods: {
    ...mapMutations('contact', [
      'setOpen',
      'setParameter',
      'setBranch',
      'setPreviousBranch',
      'setSuccess',
      'setType',
      'setIsSkipped',
      'setContactFormType',
      'setAgencyHasNoData'
    ]),
    ...mapActions('contact', ['registerContact', 'preregisterContact', 'loadPersistentInformation', 'bypassCandidateForm', 'skipContactForm']),
    onClose() {
      this.setOpen(false);
      this.setAgencyHasNoData(false);
      this.setSuccess(false);
    },
    onOpen() {
      this.setOpen(true);
    },
    disableScroll() {
      document.body.classList.add('overflow-hidden');
    },
    enableScroll() {
      document.body.classList.remove('overflow-hidden');
    },
    onSelectType(type) {
      this.setParameter({key: 'emp_cand', value: type});
      this.setType(type)
    },
    onSelectBranch(branch) {
      this.setBranch(branch);
    },
    onGotoBranchSelect() {
      if (this.branch) {
        this.setPreviousBranch(this.branch);
      }
      this.setBranch(null);
    },
    onSubmitForm() {
      this.setIsSkipped(false);
      this.setSuccess();
      //this.registerContact();
    },
    onRegisterContact() {
      this.registerContact();
    },
    onSkipForm() {
      //this.setIsSkipped(true);
      console.log('Skipping form name');
      this.skipContactForm(this.agency);
    },
    onSubmitPreregister() {
      console.log(`Current view: ${this.currentView}`);
      if (!this.agency.FAL) {
        console.log('Calling pre-register');
        this.preregisterContact();
      } else {
        console.log('Skipping pre-register as its a FREE agency');
      }
    },
    onSelectOtherOption({ method }) {
      this.setContactFormType(method);
      this.setAgencyHasNoData(false);
      this.setSuccess(false);
    },
  },
  created() {
    if (this.open) {
      this.disableScroll();
    }
  },
  beforeDestroy() {
    this.enableScroll();
  },
  mounted() {

  }
};
</script>
